import {
  ReconciliationSupplierOrder,
  useCreateOrderInvoiceApi,
  useDeleteOrderAdjustmentApi,
  useGetReconciliationSupplierOrdersApi,
  useUpdateInvoicePaymentApi,
} from 'apis/reconciliation';
import dayjs from 'dayjs';
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useCustomToast,
} from 'design-system';
import { OverrideCommissionModal } from 'pages/gpo-orders/components/override-commission-modal';
import { AdjustOrderModal } from 'pages/reconciliation/components/adjust-order-modal';
import { InvoiceStatusChip } from 'pages/reconciliation/components/invoice-status-chip';
import { MarkPaidPopover } from 'pages/reconciliation/components/mark-paid-popover';
import { getAdjustmentTooltip, isOrderInvoiceStale } from 'pages/reconciliation/utils/adjustments';
import { formatAed } from 'pages/reconciliation/utils/format';
import { getOrdersTotals } from 'pages/reconciliation/utils/invoices';
import { formatMonthLabel } from 'pages/reconciliation/utils/month';
import { ChangeEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FiFileText, FiMoreVertical, FiRefreshCw } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

export interface ISupplierOrdersModal {
  isOpen: boolean;
  onClose: () => void;
  month: string;
  supplierId: number;
  supplierName: string;
  /** null = all restaurants (supplier-wide). */
  restaurantId: number | null;
  restaurantName: string | null;
  defaultUnpaidOnly?: boolean;
}

/** Dense per-order table for one supplier-month (optionally one restaurant): order facts,
 * expandable item lines, invoice chip, the per-invoice payment ledger actions, and the
 * reconciliation-only overlay actions (adjust lines / cancel a line / typed total, per-order
 * commission edit or waiver). The "Unpaid only" toggle shows exactly the set the monthly
 * consolidation invoice will bill. */
export const SupplierOrdersModal = ({
  isOpen,
  onClose,
  month,
  supplierId,
  supplierName,
  restaurantId,
  restaurantName,
  defaultUnpaidOnly = false,
}: ISupplierOrdersModal) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [unpaidOnly, setUnpaidOnly] = useState(defaultUnpaidOnly);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Record<number, boolean>>({});

  // Reset per-open state, and invalidate on open so presigned PDF URLs (~15 min expiry) are
  // never served from a stale cache — a stale link fails with an S3 XML error users read as
  // "broken invoice". On first open there is nothing cached, so this is a no-op then.
  // Deliberately keyed on isOpen alone — the other values only matter at the moment of opening.
  useEffect(() => {
    if (!isOpen) return;
    setUnpaidOnly(defaultUnpaidOnly);
    setExpandedOrderIds({});
    queryClient.invalidateQueries(['reconciliation-supplier-orders', month, supplierId]);
  }, [isOpen]);

  const {
    data: orders = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetReconciliationSupplierOrdersApi(month, isOpen ? supplierId : null, restaurantId);

  // Payment changes ripple into the derived collected amount → detail, list row, and KPIs.
  const invalidate = () => {
    queryClient.invalidateQueries(['reconciliation-supplier-orders', month, supplierId]);
    queryClient.invalidateQueries(['reconciliation-supplier-details', month, supplierId]);
    queryClient.invalidateQueries(['reconciliation-suppliers']);
    queryClient.invalidateQueries(['reconciliation-summary', month]);
  };

  const { mutate: updatePayment } = useUpdateInvoicePaymentApi({
    onSuccess: () => {
      toast({ description: 'Marked as unpaid — the month’s collected amount has been updated', status: 'success' });
      invalidate();
    },
  });

  // Finance-side overlay per order (adjusted lines / cancelled lines / typed total) — never the
  // order itself. State-as-disclosure: the order being adjusted doubles as the isOpen flag.
  const [adjustingOrder, setAdjustingOrder] = useState<ReconciliationSupplierOrder | null>(null);
  const { mutate: deleteAdjustment } = useDeleteOrderAdjustmentApi({
    onSuccess: () => {
      toast({ description: 'Adjustment removed — the order reconciles at its original figures', status: 'success' });
      invalidate();
    },
  });

  // Backfill for pre-rollout orders (no invoice, terms stamped): generate server-side, or
  // upload an existing PDF via one shared hidden input — uploadOrderIdRef remembers which
  // row asked for the file dialog.
  const [creatingOrderId, setCreatingOrderId] = useState<number | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadOrderIdRef = useRef<number | null>(null);

  const { mutate: createInvoice } = useCreateOrderInvoiceApi({
    onSuccess: ({ invoice }) => {
      toast({ description: `Invoice ${invoice.invoiceNumber} added to the ledger`, status: 'success' });
      invalidate();
    },
    onSettled: () => setCreatingOrderId(null),
  });

  const generateInvoiceFor = (orderId: number) => {
    setCreatingOrderId(orderId);
    createInvoice({ orderId });
  };

  const uploadInvoiceFor = (orderId: number) => {
    uploadOrderIdRef.current = orderId;
    uploadInputRef.current?.click();
  };

  const onUploadFileChosen = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const orderId = uploadOrderIdRef.current;
    event.target.value = ''; // allow re-picking the same file after an error
    if (!file || orderId == null) return;
    setCreatingOrderId(orderId);
    createInvoice({ orderId, file });
  };

  const totals = useMemo(() => getOrdersTotals(orders), [orders]);
  // "Unpaid only" = not-yet-paid, including invoiceless orders — the set the monthly invoice bills.
  const visibleOrders = unpaidOnly ? orders.filter((o) => !o.invoice?.isPaid) : orders;
  const showRestaurantColumn = restaurantId === null;

  const toggleExpanded = (orderId: number) => setExpandedOrderIds((prev) => ({ ...prev, [orderId]: !prev[orderId] }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '5xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb='sm'>
          <Stack spacing='2px'>
            <HStack spacing='sm'>
              <Text>Orders — {supplierName}</Text>
              <IconButton
                aria-label='refresh-orders'
                icon={<Icon as={FiRefreshCw} boxSize='14px' />}
                size='xs'
                variant='ghost'
                isLoading={isFetching}
                onClick={() => refetch()}
              />
            </HStack>
            <Text fontSize='sm' fontWeight='normal' color='gray.500'>
              {restaurantName ?? 'All restaurants'} · {formatMonthLabel(month)} · {totals.count}{' '}
              {totals.count === 1 ? 'order' : 'orders'} · Σ commission {formatAed(totals.commission)} · paid{' '}
              {formatAed(totals.paidTotal)} / unpaid {formatAed(totals.unpaidTotal)}
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb='lg'>
          <input ref={uploadInputRef} type='file' accept='application/pdf' hidden onChange={onUploadFileChosen} />
          <HStack justify='flex-end' mb='sm' spacing='xs'>
            <Switch
              id='unpaid-only'
              size='sm'
              colorScheme='red'
              isChecked={unpaidOnly}
              onChange={(e) => setUnpaidOnly(e.target.checked)}
            />
            <Text as='label' htmlFor='unpaid-only' fontSize='sm' color='gray.600' cursor='pointer'>
              Unpaid only
            </Text>
          </HStack>

          {isLoading ? (
            <Stack spacing='sm'>
              <Skeleton height='24px' />
              <Skeleton height='24px' />
              <Skeleton height='24px' />
            </Stack>
          ) : (
            <TableContainer overflowX='auto' whiteSpace='normal'>
              <Table size='sm' w='100%'>
                <Thead>
                  <Tr>
                    <Th w='32px' />
                    <Th fontSize='xs' color='gray.400'>
                      Order
                    </Th>
                    {showRestaurantColumn && (
                      <Th fontSize='xs' color='gray.400'>
                        Restaurant
                      </Th>
                    )}
                    <Th isNumeric fontSize='xs' color='gray.400' whiteSpace='nowrap'>
                      Total
                    </Th>
                    <Th isNumeric fontSize='xs' color='gray.400'>
                      Qty
                    </Th>
                    <Th isNumeric fontSize='xs' color='gray.400' whiteSpace='nowrap'>
                      Commission
                    </Th>
                    <Th fontSize='xs' color='gray.400'>
                      Invoice
                    </Th>
                    <Th w='120px' />
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleOrders.length ? (
                    visibleOrders.map((order) => (
                      <OrderRow
                        key={order.orderId}
                        order={order}
                        showRestaurant={showRestaurantColumn}
                        isExpanded={!!expandedOrderIds[order.orderId]}
                        onToggle={() => toggleExpanded(order.orderId)}
                        onMarkUnpaid={(invoiceId) => updatePayment({ invoiceId, isPaid: false })}
                        onSaved={invalidate}
                        onGenerateInvoice={generateInvoiceFor}
                        onUploadInvoice={uploadInvoiceFor}
                        onAdjust={() => setAdjustingOrder(order)}
                        onResetAdjustment={() => deleteAdjustment({ orderId: order.orderId })}
                        isCreatingInvoice={creatingOrderId === order.orderId}
                      />
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={showRestaurantColumn ? 8 : 7} textAlign='center' color='gray.500'>
                        {unpaidOnly ? 'No unpaid orders — nothing left to bill' : 'No orders this month'}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <AdjustOrderModal order={adjustingOrder} onClose={() => setAdjustingOrder(null)} onSaved={invalidate} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface IOrderRow {
  order: ReconciliationSupplierOrder;
  showRestaurant: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onMarkUnpaid: (invoiceId: number) => void;
  onSaved: () => void;
  onGenerateInvoice: (orderId: number) => void;
  onUploadInvoice: (orderId: number) => void;
  onAdjust: () => void;
  onResetAdjustment: () => void;
  isCreatingInvoice: boolean;
}

const OrderRow = ({
  order,
  showRestaurant,
  isExpanded,
  onToggle,
  onMarkUnpaid,
  onSaved,
  onGenerateInvoice,
  onUploadInvoice,
  onAdjust,
  onResetAdjustment,
  isCreatingInvoice,
}: IOrderRow) => {
  const colSpan = showRestaurant ? 8 : 7;
  // Backfill applies only when no invoice exists AND terms are stamped (and non-zero — a
  // waived / fully-cancelled order has nothing to bill).
  const canBackfill = !order.invoice && order.commission != null && order.commission !== 0;
  // The adjust/commission actions need the extended row shape — a pre-adjustment BE sends
  // neither `adjustment` nor `orderItemId`s, so the menu simply stays hidden until it does.
  const supportsAdjustments = order.adjustment !== undefined;
  const isAdjusted = !!order.adjustment;
  const isWaived = order.commissionType === 'waived';
  const invoiceStale = isOrderInvoiceStale(order);
  const originalTotal = order.originalTotal ?? order.orderTotal;
  const originalQuantity = order.originalQuantity ?? order.totalQuantity;
  const commissionRow = {
    orderId: order.orderId,
    orderCode: order.orderCode,
    orderTotal: order.orderTotal,
    commissionType: order.commissionType ?? (order.commission != null ? ('percent' as const) : null),
    commissionRate: order.commissionRate ?? null,
    commissionAmount: order.commission,
  };

  return (
    <Fragment>
      <Tr _hover={{ bg: '#FAFBFC' }}>
        <Td w='32px' px='xs'>
          <Button size='xs' variant='ghost' onClick={onToggle} aria-label='toggle-items'>
            {isExpanded ? '−' : '+'}
          </Button>
        </Td>
        <Td whiteSpace='nowrap'>
          {/* Same code-keyed details route + new-tab idiom as delete-variant-modal — the CMS
              order route is keyed by the encoded code, not the numeric id. */}
          <Link
            as={RouterLink}
            to={`/app/orders/details/${order.orderCode}`}
            target='_blank'
            fontWeight='semibold'
            color='red.500'
            fontSize='sm'>
            {order.orderCode}
          </Link>
          <Text fontSize='xs' color='gray.400'>
            {dayjs(order.orderDate).format('DD MMM YYYY')}
          </Text>
        </Td>
        {showRestaurant && <Td wordBreak='break-word'>{order.restaurantName}</Td>}
        <Td isNumeric whiteSpace='nowrap'>
          {/* Adjusted rows show the reconciled figure with the placed figure struck through —
              the deviation from the platform order must never be invisible. */}
          {isAdjusted && originalTotal !== order.orderTotal && (
            <Text as='span' fontSize='xs' color='gray.400' textDecoration='line-through' mr='6px'>
              {formatAed(originalTotal)}
            </Text>
          )}
          {formatAed(order.orderTotal)}
          {isAdjusted && (
            <Tooltip label={getAdjustmentTooltip(order) ?? ''} placement='top' hasArrow>
              <Badge ml='6px' fontSize='2xs' textTransform='none' colorScheme='orange' verticalAlign='middle'>
                Adjusted
              </Badge>
            </Tooltip>
          )}
        </Td>
        <Td isNumeric>
          {isAdjusted && originalQuantity !== order.totalQuantity && (
            <Text as='span' fontSize='xs' color='gray.400' textDecoration='line-through' mr='6px'>
              {originalQuantity}
            </Text>
          )}
          {order.totalQuantity}
        </Td>
        <Td isNumeric whiteSpace='nowrap' color={order.commission == null || isWaived ? 'gray.400' : undefined}>
          {order.commission == null ? 'Pending' : isWaived ? 'Waived' : formatAed(order.commission)}
        </Td>
        <Td>
          <HStack spacing='xs'>
            <InvoiceStatusChip commission={order.commission} invoice={order.invoice} />
            {order.invoice && (
              <Text fontSize='xs' color='gray.400' whiteSpace='nowrap'>
                {order.invoice.invoiceNumber}
              </Text>
            )}
            {invoiceStale && (
              <Tooltip
                label={`Invoice was issued at ${formatAed(
                  order.invoice?.netAmount
                )} but the order now carries ${formatAed(
                  order.commission
                )} commission — regenerate it (or mark it unpaid first if it was paid).`}
                placement='top'
                hasArrow>
                <Badge fontSize='2xs' textTransform='none' colorScheme='orange'>
                  Stale
                </Badge>
              </Tooltip>
            )}
          </HStack>
        </Td>
        <Td>
          <HStack spacing='xs' justify='flex-end'>
            {order.invoice && (
              <Tooltip label='View PDF' placement='top' hasArrow>
                <Link href={order.invoice.url} target='_blank' rel='noopener noreferrer' display='inline-flex'>
                  <Icon as={FiFileText} boxSize='16px' color='gray.500' />
                </Link>
              </Tooltip>
            )}
            {order.invoice && !order.invoice.isPaid && (
              <MarkPaidPopover invoiceId={order.invoice.id} onSaved={onSaved} />
            )}
            {/* ONE overflow menu per row, whatever the invoice state — payment, adjustment and
                backfill actions all live here (a paid row used to carry a second menu). */}
            {(canBackfill || supportsAdjustments || order.invoice?.isPaid) && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label='order-actions'
                  icon={<Icon as={FiMoreVertical} boxSize='14px' />}
                  size='xs'
                  variant='ghost'
                  isLoading={isCreatingInvoice}
                />
                <MenuList zIndex='popover'>
                  {order.invoice?.isPaid && (
                    <>
                      <MenuItem onClick={() => order.invoice && onMarkUnpaid(order.invoice.id)}>
                        Mark as unpaid
                      </MenuItem>
                      {supportsAdjustments && <MenuDivider />}
                    </>
                  )}
                  {supportsAdjustments && (
                    <>
                      <MenuItem onClick={onAdjust}>{isAdjusted ? 'Edit adjustment…' : 'Adjust order…'}</MenuItem>
                      {isAdjusted && <MenuItem onClick={onResetAdjustment}>Reset to original order</MenuItem>}
                      <OverrideCommissionModal
                        row={commissionRow}
                        onSaved={onSaved}
                        trigger={<MenuItem>{isWaived ? 'Edit commission (waived)…' : 'Commission / waive…'}</MenuItem>}
                      />
                    </>
                  )}
                  {canBackfill && supportsAdjustments && <MenuDivider />}
                  {canBackfill && (
                    <>
                      <MenuItem onClick={() => onGenerateInvoice(order.orderId)}>Generate invoice</MenuItem>
                      <MenuItem onClick={() => onUploadInvoice(order.orderId)}>Upload invoice PDF…</MenuItem>
                    </>
                  )}
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr>
          <Td colSpan={colSpan} p='0'>
            <Box bg='#FAFBFC' px={{ base: 'sm', md: '44px' }} py='sm'>
              <Table size='sm' w='100%' variant='unstyled'>
                <Thead>
                  <Tr>
                    <Th fontSize='2xs' color='gray.400' pl='0'>
                      Item
                    </Th>
                    <Th isNumeric fontSize='2xs' color='gray.400'>
                      Qty
                    </Th>
                    <Th fontSize='2xs' color='gray.400'>
                      Unit
                    </Th>
                    <Th isNumeric fontSize='2xs' color='gray.400'>
                      Price
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {order.items.map((item, index) => {
                    const qtyChanged = item.originalQuantity != null && item.originalQuantity !== item.quantity;
                    const priceChanged = item.originalPrice != null && item.originalPrice !== item.price;
                    return (
                      <Tr key={item.orderItemId ?? index} opacity={item.isCancelled ? 0.55 : 1}>
                        <Td pl='0' fontSize='sm'>
                          <Text as='span' textDecoration={item.isCancelled ? 'line-through' : undefined}>
                            {item.nameEn}
                          </Text>
                          {item.isCancelled && (
                            <Badge ml='6px' fontSize='2xs' textTransform='none' colorScheme='red'>
                              Cancelled
                            </Badge>
                          )}
                        </Td>
                        <Td isNumeric fontSize='sm'>
                          {(qtyChanged || item.isCancelled) && (
                            <Text as='span' fontSize='xs' color='gray.400' textDecoration='line-through' mr='6px'>
                              {item.originalQuantity}
                            </Text>
                          )}
                          {item.isCancelled ? 0 : item.quantity}
                        </Td>
                        <Td fontSize='sm' color='gray.500'>
                          {item.unit}
                        </Td>
                        <Td isNumeric fontSize='sm' whiteSpace='nowrap'>
                          {priceChanged && (
                            <Text as='span' fontSize='xs' color='gray.400' textDecoration='line-through' mr='6px'>
                              {formatAed(item.originalPrice)}
                            </Text>
                          )}
                          {formatAed(item.price)}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Td>
        </Tr>
      )}
    </Fragment>
  );
};
